import {
  Button,
  Divider,
  Flex,
  Tag,
  Text,
  Tooltip,
  useBreakpointValue,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import {
  ListingComparableSetDocument,
  ListingComparableSetForBuyerOfferAnalysisDocument,
  useCreateOrUpdateListingComparableAdjustmentMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  ListingComparableSetFragment,
  MlsListingFragment,
} from '@client/graphql/__generated__/types';
import { useEffect, useRef, useState } from 'react';
import { getSaleListColor } from '~/apps/consumer/components/BuyerOfferAnalysis/KeyTermsAndContingencies/common.utils';
import { CurrencyInput } from '~/common/components/Inputs/CurrencyInput';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { formatToCurrency } from '~/services/document/utils/number';
import { BaseComparableField } from '../../../../Comparables/BaseComparableField';
import {
  ADJUSTABLE_PRICE_FIELD_HEIGHT,
  ADJUSTABLE_PRICE_FIELD_HEIGHT_MOBILE,
} from '../constants';

interface AdjustablePriceFieldProps {
  comparableSet: ListingComparableSetFragment;
  mlsListing: MlsListingFragment;
  onUpdate?: () => Promise<void> | void;
  isEditMode?: boolean;
}

export const AdjustablePriceField = ({
  comparableSet,
  mlsListing,
  onUpdate,
  isEditMode,
}: AdjustablePriceFieldProps) => {
  const isMobile = useIsMobile();
  const [isPrint] = useMediaQuery('print');
  const [updateListingComparableAdjustment] =
    useCreateOrUpdateListingComparableAdjustmentMutation();
  const [adjustedPrice, setAdjustedPrice] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const compSetAdjustedPrice = comparableSet?.listingComparables?.find(
    (comparable) => comparable.mlsListing.id === mlsListing.id
  )?.adjustments?.totalAdjustmentPrice;

  const cellHeight = useBreakpointValue({
    base: ADJUSTABLE_PRICE_FIELD_HEIGHT_MOBILE,
    md: ADJUSTABLE_PRICE_FIELD_HEIGHT,
  });

  const updateAdjustedPrice = (value: number | null) => {
    if (value !== compSetAdjustedPrice) {
      void (async () => {
        await updateListingComparableAdjustment({
          variables: {
            input: {
              listingComparableSetId: comparableSet?.id,
              mlsListingId: mlsListing?.id,
              adjustments: {
                priceAdjustment: value ?? (null as unknown as number),
              },
            },
          },
          refetchQueries: [
            // Add any other queries that need to be refetched here.
            // Its ok to have queries that are not related to the current page.
            ListingComparableSetForBuyerOfferAnalysisDocument,
            ListingComparableSetDocument,
          ],
        });

        await onUpdate?.();
      })();
    }
  };

  useEffect(() => {
    const newAdjustedPrice = comparableSet?.listingComparables?.find(
      (comparable) => comparable.mlsListing.id === mlsListing.id
    )?.adjustments?.totalAdjustmentPrice;
    setAdjustedPrice(newAdjustedPrice ?? null);
  }, [comparableSet?.listingComparables, mlsListing.id]);

  const isClosed = mlsListing?.sales?.closePrice != null;
  const priceTitle = isClosed ? 'Sold price' : 'List price';

  const priceValue = isClosed
    ? mlsListing?.sales?.closePrice
    : mlsListing?.listPrice;

  const priceText = formatToCurrency(priceValue);

  const adjustmentText =
    adjustedPrice || !isEditMode ? formatToCurrency(adjustedPrice || 0) : 'Add';

  const adjustedPriceTitle = adjustedPrice
    ? `${isMobile ? 'Adj.' : 'Adjusted'} price`
    : priceTitle;

  const adjustedPriceText = adjustedPrice
    ? formatToCurrency(priceValue ? priceValue + adjustedPrice : 0)
    : priceText;

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const priceTooltip = () => {
    if (!mlsListing.sales?.closePrice || !mlsListing.listPrice) {
      return null;
    }

    const saleList = mlsListing.sales.closePrice / mlsListing.listPrice - 1;
    const formattedSaleList = `${(saleList * 100).toFixed(1)}%`;

    const commonTagProps = {
      bg: 'whiteAlpha.200',
      fontSize: 'xs',
      h: '18px',
      minH: 'unset',
      px: 1.5,
      py: 0,
      rounded: 'xs',
    };

    const toolTipContent = (
      <VStack w="full">
        <Flex alignItems="center" justifyContent="space-between" w="full">
          <Text>List price</Text>
          <Tag {...commonTagProps}>
            {formatToCurrency(mlsListing.listPrice)}
          </Tag>
        </Flex>
        <Flex
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          w="full"
        >
          <Text>Sold price vs list price</Text>
          <Tag color={getSaleListColor(saleList)} {...commonTagProps}>
            {formattedSaleList}
          </Tag>
        </Flex>
      </VStack>
    );

    return (
      <Tooltip
        bg="black"
        border="none"
        borderRadius="sm"
        fontSize="sm"
        label={toolTipContent}
        placement="top"
        px={3}
      >
        <Tag color={getSaleListColor(saleList)} {...commonTagProps}>
          List {formattedSaleList}
        </Tag>
      </Tooltip>
    );
  };

  return (
    <>
      <BaseComparableField height={cellHeight}>
        <Flex direction="column" px={4} py={2} width="100%">
          <Flex alignItems="center" justifyContent="space-between">
            <Text color="whiteAlpha.700">{priceTitle}</Text>
            <Flex alignItems="center" gap={2}>
              {!isMobile && !isPrint && priceTooltip()}
              <Text color="whiteAlpha.700">{priceText}</Text>
            </Flex>
          </Flex>
          {(isMobile || isPrint) && isClosed && (
            <Flex alignItems="center" justifyContent="space-between">
              <Text color="whiteAlpha.700">Sale vs List</Text>
              <Flex alignItems="center" gap={2}>
                {priceTooltip()}
              </Flex>
            </Flex>
          )}
          <Flex alignItems="center" gap={16} justifyContent="space-between">
            <Text color="whiteAlpha.700">
              {isMobile || isPrint ? 'Adj.' : 'Adjustment'}
            </Text>
            {!isEditMode && (
              <Text color="whiteAlpha.700">{adjustmentText}</Text>
            )}
            {isEditMode &&
              (isEditing ? (
                <CurrencyInput
                  ref={inputRef}
                  borderRadius="0"
                  height="24px"
                  paddingRight={0}
                  textAlign="right"
                  value={adjustedPrice ?? undefined}
                  width="100%"
                  onBlur={() => {
                    updateAdjustedPrice(adjustedPrice);
                    setIsEditing(false);
                  }}
                  onKeyDown={(e) => {
                    //prevent dnd keyboard sensor from triggering
                    e.stopPropagation();

                    if (e.key === 'Enter') {
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  onMouseDown={(e) => {
                    // prevent drag and drop from triggering and unfocusing input
                    e.stopPropagation();
                  }}
                  onValueChange={(value: string | undefined) => {
                    const parsedValue = value ? parseFloat(value) : null;
                    setAdjustedPrice(parsedValue);
                  }}
                />
              ) : (
                <Button
                  _hover={{
                    color: 'cyan.700',
                  }}
                  color="cyan.500"
                  minWidth="0"
                  p={0}
                  textDecoration="underline"
                  variant="link"
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  onMouseDown={(e) => {
                    // prevent drag and drop from triggering and unfocusing input
                    e.stopPropagation();
                  }}
                >
                  {adjustmentText}
                </Button>
              ))}
          </Flex>
          <Divider py={1} />

          <Flex alignItems="center" justifyContent="space-between">
            <Text color="whiteAlpha.700">{adjustedPriceTitle}</Text>
            <Text fontSize="lg" fontWeight="medium">
              {adjustedPriceText}
            </Text>
          </Flex>
        </Flex>
      </BaseComparableField>
    </>
  );
};
